import { createRoute, redirect } from "@tanstack/react-router";
import React from "react";
import { rootRoute } from "../../app";
import { tenantService } from "../../shared/api/tenant.service";
import { useOnboardingStore } from "../../shared/store";
import OnboardingLayout from "../Layout/OnboardingLayout";

const OnboardingRoot: React.FC = () => {
  return <OnboardingLayout />;
};

interface OnboardingContext {
  onboardingStatus?: any;
}

export const onboardingRootRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding",
  component: OnboardingRoot,
  beforeLoad: async ({ location }): Promise<OnboardingContext> => {
    // Get onboarding user from sessionStorage
    const onboardingUserStr = sessionStorage.getItem("onboardingUser");
    if (!onboardingUserStr) {
      // No onboarding user found, let them proceed to instructions
      return {};
    }

    // =========================================================================
    // STEP 1: Parse onboardingUser from sessionStorage
    // Separated try-catch for better error identification and cleanup
    // =========================================================================
    let onboardingUser;
    try {
      onboardingUser = JSON.parse(onboardingUserStr);
    } catch {
      // JSON parsing failed - session data is corrupt
      sessionStorage.removeItem("onboardingUser");
      sessionStorage.removeItem("onboardingToken");
      return {};
    }

    const uuid = onboardingUser?.uuid;
    if (!uuid) {
      // No UUID found, let them proceed to instructions
      return {};
    }

    // =========================================================================
    // STEP 2: Parse onboardingData from sessionStorage
    // Non-critical - if corrupt, clear it but continue with empty object
    // =========================================================================
    let onboardingData: Record<string, unknown> = {};
    const onboardingDataStr = sessionStorage.getItem("onboardingData");
    if (onboardingDataStr) {
      try {
        onboardingData = JSON.parse(onboardingDataStr);
      } catch {
        sessionStorage.removeItem("onboardingData");
        // Continue with empty object - user can re-enter data
      }
    }

    // Check if we're already on a specific onboarding page
    // If so, skip the redirect check to allow navigation
    if (
      location.pathname.includes("/workday") ||
      location.pathname.includes("/domain") ||
      location.pathname.includes("/sso") ||
      location.pathname.includes("/completion")
    ) {
      return {};
    }

    // Only check status if we're on /onboarding root (not a specific sub-page)
    if (
      location.pathname !== "/onboarding" &&
      location.pathname !== "/onboarding/"
    ) {
      return {};
    }

    try {
      // Fetch onboarding status from API
      const statusResponse = await tenantService.getOnboardingStatus(uuid);

      // Check if the onboarding link has expired
      if (statusResponse.expired === true) {
        // Clear stored credentials and redirect to login with error
        sessionStorage.removeItem("onboardingUser");
        sessionStorage.removeItem("onboardingToken");
        sessionStorage.removeItem("onboardingData");

        sessionStorage.setItem(
          "onboardingExpiredMessage",
          statusResponse.message ||
            "This onboarding link has expired. Please contact your Rooster point of contact for a new link."
        );

        throw redirect({ to: "/login" });
      }

      if (statusResponse.success && statusResponse.data) {
        const {
          status,
          tenantId,
          onboardingData: savedData
        } = statusResponse.data;

        // Restore UUID and tenantId to Zustand store
        const store = useOnboardingStore.getState();
        if (uuid && !store.uuid) {
          store.setUuid(uuid);
        }
        if (tenantId && !store.tenantId) {
          store.setTenantId(tenantId);
        }

        // Restore onboardingData to sessionStorage if available
        if (savedData) {
          const mergedData = {
            ...onboardingData,
            ...savedData,
            tenantId: tenantId
          };
          sessionStorage.setItem("onboardingData", JSON.stringify(mergedData));

          // Also restore instructions data to Zustand store if available
          if (savedData.workday_region) {
            store.setInstructionsData({
              workdayRegion: savedData.workday_region,
              zoomConnected: savedData.zoom_connected || "",
              targetEnvironment: savedData.target_environment || ""
            });
          }
        }

        // Redirect based on status
        switch (status) {
          case "WORKDAY_CONFIGURED":
            throw redirect({ to: "/onboarding/domain" });
          case "DOMAIN_CONFIGURED":
            throw redirect({ to: "/onboarding/sso" });
          case "SSO_CONFIGURED":
          case "COMPLETED":
            throw redirect({ to: "/onboarding/completion" });
          default:
            // Status is "Request Sent" or "In Progress", proceed to workday
            break;
        }
      }
    } catch (error: unknown) {
      // IMPORTANT: TanStack Router redirects are thrown as errors and MUST be rethrown
      if (
        typeof error === "object" &&
        error !== null &&
        ("isRedirect" in error || "redirect" in error)
      ) {
        throw error;
      }

      // API failures: don't block navigation — let them proceed to the onboarding flow
      console.error("Error checking onboarding status:", error);
    }

    return {};
  }
});
